/**
 * useStockChipCancellations - Gestão de Chip › Cancelamento (operações em lote)
 *
 * 100% via RPCs: validação em massa, lotes por broker, protocolo e ação única
 * (confirmar/recusar/desistir) com exceções opcionais. Regras BR-EST-050..057.
 *
 * RELEASE: 2026-10-14 v1.2.0 - Economia mensal, filtros e carregamento de abas sob demanda.
 * RELEASE: 2026-10-14 v1.1.0 - Linha do tempo (get_stock_chip_cancellation_events) e cancelamento aberto do chip (selo da ficha).
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { stockRpc } from '@/hooks/useStockMovements';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useStockChipCancellations');

/** Tamanho do bloco enviado ao servidor na validação (barra de progresso). */
export const CANCEL_VALIDATE_CHUNK = 1000;
/** Limite de chips por operação (espelha o servidor). */
export const CANCEL_MAX_CHIPS = 5000;

export type CancelItemStatus = 'pre_request' | 'requested' | 'confirmed' | 'rejected' | 'withdrawn';
export type CancelGroupStatus = CancelItemStatus | 'partially_confirmed';

export const CANCEL_STATUS: Record<CancelGroupStatus, { label: string; tone: 'amber' | 'primary' | 'emerald' | 'destructive' | 'muted' }> = {
  pre_request: { label: 'Pré-solicitação', tone: 'amber' },
  requested: { label: 'Solicitado', tone: 'primary' },
  partially_confirmed: { label: 'Confirmado parcial', tone: 'primary' },
  confirmed: { label: 'Confirmado', tone: 'emerald' },
  rejected: { label: 'Recusado', tone: 'destructive' },
  withdrawn: { label: 'Desistência', tone: 'muted' },
};

/** Chip aceito na validação (vai para a lista do lote). */
export interface CancelCandidate {
  chip_id: string;
  iccid: string;
  line_number: string | null;
  carrier_name: string | null;
  data_plan_name: string | null;
  supplier_person_id: string;
  supplier_name: string | null;
}
export interface CancelRejected { value: string; iccid: string | null; reason: string }

/** Valores mensais agregados no servidor, separados por etapa. */
export interface CancelEconomy {
  pending_savings: number; confirmed_savings: number; pending_unpriced: number; confirmed_unpriced: number;
  pending_items: number; confirmed_items: number; confirmed_at: string | null;
}
export interface CancelAudit { created_at: string; updated_at: string; created_by_name: string | null; updated_by_name: string | null }
export interface CancelBatch extends CancelEconomy, CancelAudit {
  id: string; status: CancelGroupStatus; notes: string | null; created_at: string;
  supplier_person_id: string; supplier_name: string | null; protocol_id: string | null;
  protocol_number: string | null; sent_at: string | null; created_by_name: string | null; total_items: number;
}
export interface CancelProtocol extends CancelEconomy, CancelAudit {
  id: string; protocol_number: string; sent_at: string; status: CancelGroupStatus; notes: string | null;
  supplier_name: string | null; total_items: number; confirmed_items: number; pending_items: number; rejected_items: number;
}
/** Discriminador da listagem unificada; lotes protocolados não aparecem duas vezes. */
export type CancelGroupRow = (CancelBatch & { kind: 'batch' }) | (CancelProtocol & { kind: 'protocol' });
export type CancelListMode = 'all' | 'batches' | 'protocols';
export interface CancelItemRow {
  id: string; status: CancelItemStatus; reason: string | null; confirmed_at: string | null; created_at: string;
  batch_id: string; protocol_id: string | null; iccid: string; line_number: string | null; imsi: string | null;
  monthly_amount: number | null; price_source?: CancelPriceSource | null; price_captured_at?: string | null; price_phase?: 'estimate' | 'final' | null; carrier_name: string | null; activated_at: string | null; data_plan_name: string | null; supplier_name: string | null;
  protocol_number: string | null; sent_at: string | null;
}
export interface CancelItemsFilters {
  search: string; status: CancelItemStatus | null; batchId: string | null; protocolId: string | null; page: number; pageSize: number;
}
export interface CancelKpis { pre_request: number; requested: number; confirmed: number; rejected: number; total_batches: number; active_protocols: number; pending_savings: number; confirmed_savings: number; pending_unpriced: number; confirmed_unpriced: number }

/** Separa o texto colado em valores (linha, vírgula, ponto e vírgula, tab ou espaço). */
export function parseChipValues(text: string): string[] {
  return Array.from(new Set(text.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean).map((v) => v.slice(0, 60))));
}

/** Agrupa os chips do lote por broker (mostra "Broker A: 900 · Broker B: 348"). */
export function groupBySupplier(items: CancelCandidate[]) {
  const map = new Map<string, { supplier_person_id: string; supplier_name: string; total: number }>();
  for (const c of items) {
    const g = map.get(c.supplier_person_id) ?? { supplier_person_id: c.supplier_person_id, supplier_name: c.supplier_name ?? 'Sem nome', total: 0 };
    g.total += 1;
    map.set(c.supplier_person_id, g);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

/** Mescla novos aceitos na lista sem duplicar chips. */
export function mergeCandidates(current: CancelCandidate[], incoming: CancelCandidate[]) {
  const seen = new Set(current.map((c) => c.chip_id));
  const added = incoming.filter((c) => !seen.has(c.chip_id) && seen.add(c.chip_id));
  return { list: [...current, ...added], added: added.length };
}

const CANCEL_KEYS = ['chip-cancel-items', 'chip-cancel-batches', 'chip-cancel-protocols', 'chip-cancel-events', 'stock-items'];

function useTenantId() {
  const { currentTenant } = useAuth();
  return currentTenant?.id ?? null;
}

/** Valida valores em blocos de 1.000, informando o progresso. */
export function useValidateChipValues() {
  const tenantId = useTenantId();
  return async (values: string[], onProgress?: (done: number, total: number) => void) => {
    const accepted: CancelCandidate[] = [];
    const rejected: CancelRejected[] = [];
    for (let i = 0; i < values.length; i += CANCEL_VALIDATE_CHUNK) {
      const chunk = values.slice(i, i + CANCEL_VALIDATE_CHUNK);
      const { data, error } = await stockRpc('validate_stock_chip_cancellation_values', { p_values: chunk, p_tenant_hint: tenantId });
      if (error) {
        logger.error('Falha ao validar chips', { error: error.message });
        throw new Error(error.message);
      }
      const r = data as { accepted: CancelCandidate[]; rejected: CancelRejected[] };
      accepted.push(...r.accepted);
      rejected.push(...r.rejected);
      onProgress?.(Math.min(i + chunk.length, values.length), values.length);
    }
    return { accepted, rejected };
  };
}

/** Pesquisa instantânea de chips elegíveis. */
export function useChipCancelSearch(search: string) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-search', tenantId, search],
    enabled: !!tenantId && search.trim().length >= 3,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await stockRpc('search_stock_chip_cancellation_candidates', { p_search: search.trim(), p_tenant_hint: tenantId });
      if (error) throw new Error(error.message);
      return (data ?? []) as (CancelCandidate & { location_name: string | null })[];
    },
  });
}

export function useChipCancelBatches(status: CancelGroupStatus | null, page: number, pageSize: number, search = '', enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-batches', tenantId, status, page, pageSize, search],
    enabled: !!tenantId && enabled,
    placeholderData: (p) => p,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_chip_cancellation_batches', { p_status: status, p_page: page, p_page_size: pageSize, p_search: search || null, p_tenant_hint: tenantId });
      if (error) throw new Error(error.message);
      return data as { total: number; items: CancelBatch[] };
    },
  });
}

export function useChipCancelProtocols(search: string, page: number, pageSize: number, status: CancelGroupStatus | null = null, enabled = true, listMode: CancelListMode = 'protocols') {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-protocols', tenantId, search, page, pageSize, status, listMode],
    enabled: !!tenantId && enabled,
    placeholderData: (p) => p,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_chip_cancellation_protocols', { p_search: search || null, p_page: page, p_page_size: pageSize, p_status: status, p_list_mode: listMode, p_tenant_hint: tenantId });
      if (error) throw new Error(error.message);
      // O modo legado de protocolos recebe seu discriminador sem alterar o contrato SQL original.
      const result = data as { total: number; items: CancelGroupRow[] };
      return { ...result, items: result.items.map(record => ({ ...record, kind: record.kind ?? 'protocol' })) as CancelGroupRow[] };
    },
  });
}

/** Argumentos da RPC de chips (compartilhados com a exportação). */
export const cancelItemsRpcArgs = (f: Omit<CancelItemsFilters, 'page' | 'pageSize'>) => ({
  p_search: f.search || null, p_status: f.status, p_batch_id: f.batchId, p_protocol_id: f.protocolId,
});

export function useChipCancelItems(filters: CancelItemsFilters, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-items', tenantId, filters],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_chip_cancellations', {
        ...cancelItemsRpcArgs(filters), p_page: filters.page, p_page_size: filters.pageSize, p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);
      return data as { total: number; kpis: CancelKpis; items: CancelItemRow[] };
    },
  });
}

/** Mutações em lote (cada uma vale para todos os chips do lote/protocolo). */
export function useChipCancelMutations() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const refresh = () => CANCEL_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const onError = (e: Error) => toast.error(e.message);

  const createBatches = useMutation({
    mutationFn: async (v: { chipIds: string[]; notes?: string }) => {
      const { data, error } = await stockRpc('create_stock_chip_cancellation_batches', { p_chip_ids: v.chipIds, p_notes: v.notes || null, p_tenant_hint: tenantId });
      if (error) throw new Error(error.message);
      return data as { batch_id: string; supplier_name: string; total: number }[];
    },
    onSuccess: (r) => { refresh(); toast.success(`${r.reduce((s, b) => s + b.total, 0)} chip(s) em pré-solicitação (${r.length} lote(s))`); },
    onError,
  });

  const createProtocol = useMutation({
    mutationFn: async (v: { batchIds: string[]; number: string; sentAt: string | null; notes?: string; removeIneligible?: boolean }) => {
      const { data, error } = await stockRpc('create_stock_chip_cancellation_protocol', {
        p_batch_ids: v.batchIds, p_protocol_number: v.number, p_sent_at: v.sentAt, p_notes: v.notes || null,
        p_remove_ineligible: !!v.removeIneligible, p_tenant_hint: tenantId,
      });
      if (error) {
        const err = new Error(error.message) as Error & { hint?: string };
        err.hint = (error as { hint?: string }).hint;
        throw err;
      }
      return data as { protocol_id: string; items: number; removed: number };
    },
    onSuccess: (r) => { refresh(); toast.success(`Protocolo gerado com ${r.items} chip(s)${r.removed ? ` (${r.removed} retirado(s))` : ''}`); },
  });

  const resolve = useMutation({
    mutationFn: async (v: {
      targetType: 'batch' | 'protocol'; targetId: string; action: 'confirm' | 'reject' | 'withdraw';
      reason?: string; cancelledAt?: string | null; exceptions?: string[]; exceptionAction?: 'reject' | 'keep' | null;
    }) => {
      const { data, error } = await stockRpc('resolve_stock_chip_cancellation', {
        p_target_type: v.targetType, p_target_id: v.targetId, p_action: v.action, p_reason: v.reason || null,
        p_cancelled_at: v.cancelledAt || null, p_exception_values: v.exceptions?.length ? v.exceptions : null,
        p_exception_action: v.exceptions?.length ? v.exceptionAction : null, p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);
      return data as { updated: number; exceptions: number };
    },
    onSuccess: (r) => { refresh(); toast.success(`${r.updated} chip(s) atualizados${r.exceptions ? ` · ${r.exceptions} exceção(ões)` : ''}`); },
    onError,
  });

  return { createBatches, createProtocol, resolve };
}

/** Evento da linha do tempo de um lote/protocolo. */
export interface CancelEvent { id: string; event_type: string; details: Record<string, unknown>; created_at: string; created_by_name: string | null }

export const CANCEL_EVENT_LABEL: Record<string, string> = {
  batch_created: 'Lote criado (pré-solicitação)',
  protocol_created: 'Protocolo gerado',
  confirm: 'Cancelamento confirmado',
  reject: 'Recusado',
  withdraw: 'Desistência',
};

/** Linha do tempo (só consulta com o diálogo aberto). */
export function useChipCancelEvents(target: { batchId?: string | null; protocolId?: string | null } | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-events', tenantId, target?.batchId ?? null, target?.protocolId ?? null],
    enabled: !!tenantId && !!target && !!(target.batchId || target.protocolId),
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_chip_cancellation_events', {
        p_batch_id: target?.batchId ?? null, p_protocol_id: target?.protocolId ?? null, p_tenant_hint: tenantId,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as CancelEvent[];
    },
  });
}

/**
 * Cancelamento aberto de um chip (selo da ficha). Reaproveita a consulta oficial
 * `get_stock_chip_cancellations` pela busca do ICCID; sem permissão, não mostra nada.
 */
export function useChipOpenCancellation(iccid: string | null | undefined, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['chip-cancel-items', tenantId, 'open-by-iccid', iccid],
    enabled: !!tenantId && !!iccid && enabled,
    retry: false,
    queryFn: async () => {
      const { data, error } = await stockRpc('get_stock_chip_cancellations', {
        p_search: iccid, p_status: null, p_batch_id: null, p_protocol_id: null, p_page: 1, p_page_size: 10, p_tenant_hint: tenantId,
      });
      if (error) return null;
      const items = ((data as { items?: CancelItemRow[] })?.items ?? []);
      return items.find((i) => i.iccid === iccid && (i.status === 'pre_request' || i.status === 'requested')) ?? null;
    },
  });
}

/** Origem do valor gravado no cancelamento (snapshot imutável, BR-EST-061..064). */
export type CancelPriceSource = 'negotiated' | 'original' | 'none' | 'backfill';
export const PRICE_SOURCE_LABEL: Record<CancelPriceSource, string> = {
  negotiated: 'Valor negociado', original: 'Valor original', none: 'Sem valor informado', backfill: 'Carga retroativa',
};
